import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronUp, Download, Lock, RefreshCw, Send } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { PayslipBreakdown, type PayrollLine } from '../components/PayslipBreakdown';
import { BranchPicker } from '../components/ui/BranchPicker';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchInput } from '../components/ui/SearchInput';
import { api } from '../lib/api';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { useDebounced } from '../lib/useDebounced';
import { Loader } from '../components/ui/Spinner';

type PayrollRun = { id: string; periodMonth: string; status: string; staffCount: number; totalGrossEarnings: string; totalDeductions: string; totalNetPay: string; failureReason: string | null };
type LineRow = PayrollLine & { name: string; employeeCode: string; branchName: string | null };

const STATUS_STYLE: Record<string, string> = { generating: 'bg-amber-100 text-amber-700', draft: 'bg-slate-100 text-slate-700', generation_failed: 'bg-red-50 text-red-700', locked: 'bg-brand-100 text-brand-700', released: 'bg-emerald-100 text-emerald-700' };
const STATUS_LABEL: Record<string, string> = { generating: 'Generating…', draft: 'Draft', generation_failed: 'Generation failed', locked: 'Locked', released: 'Released' };
const money = (v: string) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (period: string) => new Date(`${period}T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

function OverrideForm({ runId, line, onDone }: { runId: string; line: LineRow; onDone: () => void }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [overrideNetPay, setOverrideNetPay] = useState(line.overrideNetPay ?? line.netPay);
  const [overrideNote, setOverrideNote] = useState(line.overrideNote ?? '');

  const save = useMutation({
    mutationFn: () => api.patch(`/api/payroll-runs/${runId}/lines/${line.id}`, { overrideNetPay, overrideNote }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['payroll-runs', runId, 'lines'] }); success('Net pay overridden'); onDone(); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not save override')),
  });
  const clear = useMutation({
    mutationFn: () => api.patch(`/api/payroll-runs/${runId}/lines/${line.id}`, { overrideNetPay: null, overrideNote: 'Reverted to computed value' }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['payroll-runs', runId, 'lines'] }); success('Override cleared'); onDone(); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not clear override')),
  });

  return (
    <form className="mt-4 grid gap-3 border-t border-border pt-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
      <label className="label">Override net pay (₹)<input type="number" step="0.01" className="field mt-1" required value={overrideNetPay} onChange={(e) => setOverrideNetPay(e.target.value)} /></label>
      <label className="label">Reason<input className="field mt-1" required minLength={3} placeholder="Why is this different from the computed value?" value={overrideNote} onChange={(e) => setOverrideNote(e.target.value)} /></label>
      <div className="flex items-end gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={save.isPending}>Save override</button>
        {line.isOverridden && <button type="button" className="btn-secondary" disabled={clear.isPending} onClick={() => clear.mutate()}>Revert to computed</button>}
        <button type="button" className="btn-secondary" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function LineCard({ runId, line, canEdit }: { runId: string; line: LineRow; canEdit: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const netPay = line.overrideNetPay ?? line.netPay;

  return (
    <article className="card">
      <button type="button" className="flex w-full items-center justify-between gap-3 text-left" onClick={() => setExpanded(!expanded)}>
        <div className="min-w-0">
          <strong className="flex items-center gap-2 truncate">{line.name}{line.hasWarning && <AlertTriangle size={14} className="shrink-0 text-amber-600" />}</strong>
          <p className="mt-0.5 truncate text-sm text-muted">{line.employeeCode}{line.branchName ? ` • ${line.branchName}` : ''}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right"><strong>{money(netPay)}</strong>{line.isOverridden && <p className="text-xs text-muted">overridden</p>}</div>
          {expanded ? <ChevronUp size={18} className="text-muted" /> : <ChevronDown size={18} className="text-muted" />}
        </div>
      </button>
      {expanded && (
        <div className="mt-4 border-t border-border pt-4">
          <PayslipBreakdown line={line} />
          {canEdit && !overriding && <button className="btn-secondary mt-4" onClick={() => setOverriding(true)}>Override net pay</button>}
          {canEdit && overriding && <OverrideForm runId={runId} line={line} onDone={() => setOverriding(false)} />}
        </div>
      )}
    </article>
  );
}

export function PayrollRunPage({ id }: { id: string }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q);
  const [branchId, setBranchId] = useState('');
  const [warningsOnly, setWarningsOnly] = useState(false);

  const run = useQuery({
    queryKey: ['payroll-runs', id],
    queryFn: async () => (await api.get<PayrollRun>(`/api/payroll-runs/${id}`)).data,
    refetchInterval: (query) => (query.state.data?.status === 'generating' ? 2000 : false),
  });
  const lines = useQuery({
    queryKey: ['payroll-runs', id, 'lines', { q: debouncedQ, branchId, warningsOnly }],
    queryFn: async () => (await api.get<LineRow[]>(`/api/payroll-runs/${id}/lines`, { params: { q: debouncedQ || undefined, branchId: branchId || undefined, hasWarning: warningsOnly || undefined } })).data,
    enabled: run.data ? run.data.status !== 'generating' : false,
  });

  const regenerate = useMutation({
    mutationFn: () => api.post(`/api/payroll-runs/${id}/regenerate`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['payroll-runs', id] }); void client.invalidateQueries({ queryKey: ['payroll-runs', id, 'lines'] }); success('Regenerating…'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not regenerate')),
  });
  const lock = useMutation({
    mutationFn: () => api.post(`/api/payroll-runs/${id}/lock`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['payroll-runs', id] }); success('Payroll run locked'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not lock run')),
  });
  const release = useMutation({
    mutationFn: () => api.post(`/api/payroll-runs/${id}/release`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['payroll-runs', id] }); success('Payslips released to staff'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not release')),
  });

  if (run.isLoading) return <Loader />;
  if (!run.data) return <div className="card text-center text-muted">Payroll run not found.</div>;
  const data = run.data;
  const rows = lines.data ?? [];
  const warningCount = rows.filter((r) => r.hasWarning).length;
  const exportBase = `${api.defaults.baseURL}/api/payroll-runs/${id}/export`;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">{monthLabel(data.periodMonth)}</h1>
          <p className="mt-1 flex items-center gap-2 text-muted"><span className={`badge ${STATUS_STYLE[data.status] ?? ''}`}>{STATUS_LABEL[data.status] ?? data.status}</span>{data.staffCount} staff</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(data.status === 'draft' || data.status === 'generation_failed') && <button className="btn-secondary" disabled={regenerate.isPending} onClick={() => regenerate.mutate()}><RefreshCw size={16} /> Regenerate</button>}
          {data.status === 'draft' && <button className="btn-primary" disabled={lock.isPending} onClick={() => { if (confirm(`Lock ${monthLabel(data.periodMonth)}'s payroll? This is irreversible — loan balances will be deducted and no further edits will be possible.`)) lock.mutate(); }}><Lock size={16} /> Lock run</button>}
          {data.status === 'locked' && <button className="btn-primary" disabled={release.isPending} onClick={() => { if (confirm('Release payslips to all staff now?')) release.mutate(); }}><Send size={16} /> Release payslips</button>}
          {(data.status === 'locked' || data.status === 'released') && (
            <>
              <a className="btn-secondary" href={`${exportBase}/bank-csv`} target="_blank" rel="noreferrer"><Download size={16} /> Bank reference CSV</a>
              <a className="btn-secondary" href={`${exportBase}/statutory-csv`} target="_blank" rel="noreferrer"><Download size={16} /> PF/ESI/PT/LWF CSV</a>
            </>
          )}
        </div>
      </div>

      {data.status === 'generating' && (
        <div className="card flex flex-col items-center gap-2 py-12 text-center">
          <RefreshCw size={28} className="animate-spin text-brand-600" />
          <p className="font-semibold">Computing this run…</p>
          <p className="text-sm text-muted">Attendance, leave, and loan data for every staff member is being processed in the background.</p>
        </div>
      )}

      {data.status === 'generation_failed' && (
        <div className="card flex items-start gap-3 border border-red-200 bg-red-50">
          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600" />
          <div><p className="font-semibold text-red-800">Generation failed</p><p className="mt-1 text-sm text-red-700">{data.failureReason ?? 'Unknown error.'}</p></div>
        </div>
      )}

      {data.status !== 'generating' && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="card py-3 text-center"><p className="text-xl font-bold">{money(data.totalGrossEarnings)}</p><p className="text-xs text-muted">Gross earnings</p></div>
            <div className="card py-3 text-center"><p className="text-xl font-bold text-red-700">{money(data.totalDeductions)}</p><p className="text-xs text-muted">Deductions</p></div>
            <div className="card py-3 text-center"><p className="text-xl font-bold text-brand-700">{money(data.totalNetPay)}</p><p className="text-xs text-muted">Net pay</p></div>
            <div className="card py-3 text-center"><p className="text-xl font-bold">{warningCount}</p><p className="text-xs text-muted">Warnings</p></div>
          </div>

          <div className="mb-4 grid gap-3 sm:grid-cols-4">
            <SearchInput className="sm:col-span-2" placeholder="Search by name or code…" value={q} onChange={(e) => setQ(e.target.value)} />
            <BranchPicker className="mt-0" clearLabel="All branches" placeholder="Filter by branch…" value={branchId} onChange={setBranchId} />
            <button type="button" className={`rounded-full px-3 py-1.5 text-sm font-semibold ${warningsOnly ? 'bg-amber-500 text-white' : 'border border-border bg-white text-muted'}`} onClick={() => setWarningsOnly(!warningsOnly)}>
              {warningsOnly ? 'Showing warnings only' : 'Show warnings only'}
            </button>
          </div>

          {lines.isLoading && <Loader />}
          {!lines.isLoading && rows.length === 0 && <EmptyState title="No staff match these filters" />}
          <div className="space-y-3">
            {rows.map((line) => <LineCard key={line.id} runId={id} line={line} canEdit={data.status === 'draft'} />)}
          </div>
        </>
      )}
    </>
  );
}
